import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatRelativeTime } from '@/lib/time';
import {
  ai_proposal_acceptance_path,
  ai_proposal_rejection_path,
} from '@/rails/routes';
import type { AiProposal } from '@/types/ai-proposal';
import { router } from '@inertiajs/react';
import { useState } from 'react';

interface Props {
  proposals: AiProposal[];
}

export default function AiProposalsCard({ proposals }: Props) {
  if (proposals.length === 0) return null;

  return (
    <Card className="border-primary/40 bg-primary/5">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium">AI proposals</CardTitle>
        <CardDescription>
          Review status and roadmap changes. Nothing applies until you accept.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {proposals.map((proposal) => (
          <ProposalReview key={proposal.id} proposal={proposal} />
        ))}
      </CardContent>
    </Card>
  );
}

function ProposalReview({ proposal }: { proposal: AiProposal }) {
  const [now, setNow] = useState(proposal.status?.now ?? '');
  const [next, setNext] = useState(proposal.status?.next ?? '');
  const [blocked, setBlocked] = useState(proposal.status?.blocked ?? '');

  const handleAccept = () => {
    router.post(ai_proposal_acceptance_path(proposal.id), {
      status: { now, next, blocked },
    });
  };

  const handleReject = () => {
    router.post(ai_proposal_rejection_path(proposal.id));
  };

  return (
    <div className="bg-background space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="font-normal">
          {proposal.source.replace(/_/g, ' ')}
        </Badge>
        <span className="text-muted-foreground text-xs">
          {formatRelativeTime(proposal.createdAt)}
        </span>
      </div>

      <div className="space-y-2">
        <div className="space-y-1">
          <Label htmlFor={`proposal-now-${proposal.id}`}>Now</Label>
          <Textarea
            id={`proposal-now-${proposal.id}`}
            value={now}
            onChange={(event) => setNow(event.target.value)}
            rows={2}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`proposal-next-${proposal.id}`}>Next</Label>
          <Textarea
            id={`proposal-next-${proposal.id}`}
            value={next}
            onChange={(event) => setNext(event.target.value)}
            rows={2}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`proposal-blocked-${proposal.id}`}>Blocked</Label>
          <Textarea
            id={`proposal-blocked-${proposal.id}`}
            value={blocked}
            onChange={(event) => setBlocked(event.target.value)}
            rows={2}
          />
        </div>
      </div>

      {proposal.roadmapOps.length > 0 ? (
        <div className="space-y-1">
          <p className="text-sm font-medium">Roadmap ops</p>
          <ul className="text-muted-foreground list-inside list-disc text-sm">
            {proposal.roadmapOps.map((op, index) => (
              <li key={`${proposal.id}-op-${index}`}>
                {formatRoadmapOp(op.op, op.title)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex gap-2">
        <Button type="button" size="sm" onClick={handleAccept}>
          Accept
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={handleReject}>
          Reject
        </Button>
      </div>
    </div>
  );
}

function formatRoadmapOp(op: string, title?: string) {
  if (op === 'add' && title) return `Add milestone: ${title}`;
  if (op === 'complete') return 'Complete milestone';
  if (op === 'set_in_progress') return 'Set milestone in progress';
  if (op === 'reorder') return 'Reorder milestones';
  return op;
}
